import React, { useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import Breadcrumb from 'components/Common/Breadcrumb2';
import { useNavigate } from 'react-router-dom';
import {
  getRoles,
  fetchCoursesRequest,
  fetchLessonsRequest
} from 'store/actions';
import LessonTable from './LessonTable';

const LessonList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux states
  const lessons = useSelector((state) => state.Lesson.lessons);
  const loading = useSelector((state) => state.Lesson.loading);
  const error = useSelector((state) => state.Lesson.error);
  const courses = useSelector((state) => state.Course.courses);
  const roles = useSelector((state) => state?.Role.roles);

  // Permissions
  const permissions = JSON.parse(localStorage.getItem('permissions'));
  const hasAddPermission = permissions?.some(
    (item) => item?.permission_name === 'Add Lesson'
  );

  // Local states
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [searchString, setSearchString] = useState('');
  const [selectedSortData, setSelectedSortData] = useState(null);
  const [selectedCreator, setSelectedCreator] = useState(null); // ✅ for creator filter

  // ────────────────────────────────
  // INITIAL FETCH (Courses + Roles)
  // ────────────────────────────────
  useEffect(() => {
    dispatch(fetchCoursesRequest());
    dispatch(getRoles());
  }, [dispatch]);

  // ────────────────────────────────
  // FETCH LESSONS (on filter / page change)
  // ────────────────────────────────
  useEffect(() => {
    const params = {
      page: pageIndex,
      limit: pageSize,
    };

    if (selectedChapter?.value) params.chapterId = selectedChapter.value;
    if (selectedCreator?.value) params.createdBy = selectedCreator.value;
    if (selectedSortData?.value && selectedSortData?.direction)
      params.sortBy = `${selectedSortData.value}:${selectedSortData.direction}`;
    if (searchString) params.search = searchString;

    console.log('FETCH LESSONS PARAMS ===>', params);
    dispatch(fetchLessonsRequest(params));
  }, [
    dispatch,
    pageIndex,
    pageSize,
    selectedChapter,
    selectedCreator,
    selectedSortData,
    searchString,
  ]);

  // Reset pagination when filters or search change
  useEffect(() => {
    setPageIndex(1);
  }, [
    selectedCourse,
    selectedModule,
    selectedChapter,
    selectedCreator,
    searchString,
  ]);

  // ────────────────────────────────
  // Course Dropdown Options
  // ────────────────────────────────
  const courseOptions =
    courses?.data?.map((course) => ({
      value: course._id,
      label: course.title,
    })) || [];

  // ────────────────────────────────
  // Creator Dropdown Options (Cleaned)
  // ────────────────────────────────
  const creatorOptions =
    lessons?.data
      ?.map((lesson) => lesson?.createdBy)
      ?.filter(Boolean)
      ?.filter(
        (creator, i, arr) =>
          arr.findIndex((a) => a._id === creator._id) === i // unique by _id
      )
      ?.map((creator) => ({
        label: creator.name || creator.email || 'Unknown',
        value: creator._id,
      })) || [];

  console.log('creatorOptions:', creatorOptions);

  // ────────────────────────────────
  // UI
  // ────────────────────────────────
  return (
    <div className="page-content container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mx-3">
        <Breadcrumb
          title="Lessons"
          breadcrumbItems={[
            { title: 'Dashboard', link: '/dashboard' },
            { title: 'Lessons', link: '#' },
          ]}
        />
        {hasAddPermission && (
          <Button
            className="bg-primary text-white d-flex align-items-center gap-1"
            onClick={() => navigate('/lesson-add')}
          >
            <i className="ti-plus"></i> Add New
          </Button>
        )}
      </div>

      {/* Table */}
      <LessonTable
        lessons={lessons?.data || []}
        totalrows={lessons?.total}
        loading={loading}
        error={error}
        courseOptions={courseOptions}
        creatorOptions={creatorOptions}          
        selectedCreator={selectedCreator}        
        setSelectedCreator={setSelectedCreator}  
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        selectedCourse={selectedCourse}
        setSelectedCourse={setSelectedCourse}
        selectedModule={selectedModule}
        setSelectedModule={setSelectedModule}
        selectedChapter={selectedChapter}
        setSelectedChapter={setSelectedChapter}
        searchString={searchString}
        setSearchString={setSearchString}
        selectedSortData={selectedSortData}
        setSelectedSortData={setSelectedSortData}
      />
    </div>
  );
};

export default LessonList;
